import React, { useMemo } from 'react';
import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import { Typography } from '@tmap/mmm-style-guide/src/Typography';
import { badges } from '@tmap/mmm-style-guide/src/badges';
import { styled } from '@tmap/mmm-style-guide/src/styled';
import { Skeleton } from '@tmap/mmm-style-guide/src/Skeleton';
import ConditionalLazyLoad from '../conditional-lazy-load';

import { Portrait } from './portrait';

const BylineGrid = styled(Grid)(() => ({
  display: 'flex',
  alignItems: 'center',
  overflowX: 'hidden',
  textOverflow: 'ellipsis',
}));

const BylineText = styled(Typography, {
  shouldForwardProp: (p) => p !== 'colors',
})(({ theme, colors }) => ({
  overflowX: 'hidden',
  textOverflow: 'ellipsis',
  fontSize: '14px',
  display: 'inline-block',
  whiteSpace: 'nowrap',
  lineHeight: '20px',
  letterSpacing: '0.14px',
  ...(colors ? {
    color: colors(theme).title,
  } : {}),
}));

const NoOverflowGrid = styled(Grid)(() => ({
  overflowX: 'hidden',
  textOverflow: 'ellipsis',
}));

const ShowGrid = styled(Grid, {
  shouldForwardProp: (p) => p !== 'visibleBreakpoint',
})(({ theme, visibleBreakpoint }) => ({
  overflowX: 'hidden',
  textOverflow: 'ellipsis',
  display: 'none',
  [visibleBreakpoint(theme)]: {
    display: 'flex',
  },
}));

function BadgeTitlesGridItem(props) {
  const {
    Badges = [],
    visibleBreakpoint = (theme) => theme.breakpoints.up('xs'),
    gridItemProps = {},
    maxBadges = Badges.length,
  } = props;

  return (
    <ShowGrid item visibleBreakpoint={visibleBreakpoint} {...gridItemProps}>
      <NoOverflowGrid container columnSpacing={0.5}>
        {Badges.slice(0, maxBadges)}
        {Badges.length > maxBadges && (
          <BylineGrid item>
            <BylineText variant='body2' color='grey.600'>
              +{Badges.length - maxBadges} badges
            </BylineText>
          </BylineGrid>
        )}
      </NoOverflowGrid>
    </ShowGrid>
  );
}

const NameTypog = styled(Typography)(() => ({
  whiteSpace: 'nowrap',
  overflowX: 'hidden',
  textOverflow: 'ellipsis',
}));

export function MiniProfile(props) {
  const {
    data,
    showBadgeTitles = false,
    shouldLazyLoad = false,
    lazyProps = {},
  } = props;
  const skeleton = !data;
  const {
    image,
    name,
    title,
    size = 52,
    author = {},
  } = data || {};

  const portrait = useMemo(() => (
    Portrait({
      author,
      image,
      size,
    })
  ), [author, image, size]);

  const Badges = useMemo(() => {
    const filteredBadges = (author.badges || []).filter((b) => badges[b] && !badges[b].disabled);
    return filteredBadges.map((b, i) => {
      const badge = badges[b];
      return (
        <BylineGrid item key={`badge-title-${b}-${i}`}>
          <BylineText variant='body2' fontWeight={500} colors={badge.colors}>
            {badge.title}{i < filteredBadges.length - 1 ? ',' : ''}
          </BylineText>
        </BylineGrid>
      );
    });
  }, [author.badges]);
  const badgesVisible = !skeleton && showBadgeTitles && Badges.length > 0;

  return (
    <Grid
      container
      alignItems={badgesVisible ? 'start' : 'center'}
      spacing={1.5}
      flexWrap='nowrap'
    >
      {(skeleton || Boolean(portrait)) && (
        <Grid item display='flex' alignItems='center'>
          <ConditionalLazyLoad shouldLazyLoad={!skeleton && shouldLazyLoad} {...lazyProps}>
            {skeleton ? (
              <Skeleton variant='circular' width={size} height={size} />
            ) : (
              portrait
            )}
          </ConditionalLazyLoad>
        </Grid>
      )}
      <NoOverflowGrid item xs>
        <NoOverflowGrid container direction='column' spacing={0.25} paddingTop={badgesVisible ? 0.5 : 0}>
          <NoOverflowGrid item maxWidth='100% !important'>
            {skeleton ? (
              <Skeleton sx={{ fontSize: '14px' }} width='40%' />
            ) : (
              <NameTypog
                variant='subtitle1'
                className='authorName'
                fontWeight={500}
                lineHeight='20px'
              >
                {name}
              </NameTypog>
            )}
          </NoOverflowGrid>
          {badgesVisible ? (
            <BadgeTitlesGridItem
              Badges={Badges}
            />
          ) : (
            (skeleton || title) && (
              <Grid item display='flex'>
                {skeleton ? (
                  <Skeleton sx={{ fontSize: '14px' }} width='60%' />
                ) : (
                  <Typography variant='body2' className='authorTitle' color='text.secondary'>
                    {title}
                  </Typography>
                )}
              </Grid>
            )
          )}
        </NoOverflowGrid>
      </NoOverflowGrid>
    </Grid>
  );
}
